/**
 * Utility for constructing the base URI for API requests
 *
 * This module creates a properly formatted base URL using environment variables,
 * ensuring proper protocol (HTTP/HTTPS), hostname, and port configuration.
 *
 * @module api/client/utils/base
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */

import { createLogger } from '../../../utils/logger';

// Create a logger for baseUri
const logger = createLogger('ApiBaseUri');

/**
 * Constructs the base URI for API requests using environment variables
 *
 * @returns {string} The complete base URI for API requests
 */
const getBaseUri = () => {
  const scheme = process.env.REACT_APP_LEARGAS_SCHEME || 'https';
  const host = process.env.REACT_APP_LEARGAS_HOST || 'localhost';
  const port = process.env.REACT_APP_LEARGAS_PORT || '';

  // Construct the URI with proper format
  let baseUri = `${scheme}://${host}`;

  // Add port if specified and not the default for the scheme
  if (
    port &&
    !(
      (scheme === 'http' && port === '80') ||
      (scheme === 'https' && port === '443')
    )
  ) {
    baseUri += `:${port}`;
  }

  logger.info(`API Base URI constructed: ${baseUri}`);

  // Log whether we're using a secure connection
  if (scheme === 'https') {
    logger.info('Using secure HTTPS connection for API communication');
  } else {
    logger.warn(
      'Using non-secure HTTP connection for API communication - not recommended for production'
    );
  }

  return baseUri;
};

// Export a singleton instance of the baseUri
const baseUri = getBaseUri();

export default baseUri;
